import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserOffers } from '../services/offerService';
import PurchaseCard from '../components/PurchaseCard';
import '../styles/MyPurchases.css';

const MyPurchases = () => {
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }

        const fetchPurchases = async () => {
            try {
                setLoading(true);
                const data = await getUserOffers();
                setPurchases(data);
                setError(null);
            } catch (err) {
                setError('Failed to load your purchases');
                console.error('Error loading purchases:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchPurchases();
    }, [user, navigate]);

    const calculateStats = () => {
        if (purchases.length === 0) {
            return {
                totalPurchases: 0,
                totalSpent: 0,
                totalSavings: 0
            };
        }

        const totalSpent = purchases.reduce((sum, purchase) => sum + purchase.price_offer, 0);
        const totalOriginalPrice = purchases.reduce((sum, purchase) => sum + purchase.listing_price, 0);
        const totalSavings = totalOriginalPrice - totalSpent;

        return {
            totalPurchases: purchases.length,
            totalSpent,
            totalSavings
        };
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    if (!user) {
        return null;
    }

    if (loading) {
        return (
            <div className="purchases-container">
                <div className="loading">Loading your purchases...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="purchases-container">
                <div className="error">{error}</div>
            </div>
        );
    }

    const stats = calculateStats();

    return (
        <div className="purchases-container">
            <div className="purchases-header">
                <h1>My Purchases</h1>
            </div>

            {purchases.length > 0 && (
                <div className="purchases-stats">
                    <div className="stat-card">
                        <h3>Total Purchases</h3>
                        <div className="value">{stats.totalPurchases}</div>
                    </div>
                    <div className="stat-card">
                        <h3>Total Spent</h3>
                        <div className="value total-spent">{formatCurrency(stats.totalSpent)}</div>
                    </div>
                    <div className="stat-card">
                        <h3>Total Savings</h3>
                        <div className="value savings">{formatCurrency(stats.totalSavings)}</div>
                    </div>
                </div>
            )}

            {purchases.length === 0 ? (
                <div className="empty-state">
                    <p>You haven't made any purchases yet.</p>
                    <p>Start browsing listings to find great deals!</p>
                    <Link to="/listings" className="browse-listings-btn">
                        Browse Listings
                    </Link>
                </div>
            ) : (
                <div className="purchases-grid">
                    {purchases.map(purchase => (
                        <PurchaseCard 
                            key={purchase.id} 
                            purchase={purchase}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default MyPurchases; 